import { OrderedListOutlined } from "@ant-design/icons";
import { AggregatedNode } from "@p0security/shared/graph/aggregate";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  AnyAggregates,
  AssessmentNodes,
  TargetNodeType,
} from "@p0security/shared/types/assessment/data/index";
import { ZeroStatePage } from "components/Dashboard/components/common/ZeroStatePage";
import { EnvironmentDocsContext } from "components/Environment/contexts/EnvironmentDocsContext";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { VerticalSpacedDiv } from "components/divs";
import { DOCS_URLS } from "constants/docsUrls";
import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router";

import { AssessmentGraph } from "../components/AssessmentGraph";
import { Inventory } from "../components/Inventory";
import { ScopeSelect } from "../components/ScopeSelect";
import { DetailsColumn } from "../components/cells/DetailsColumn";
import { AggregatedGraphContext } from "../contexts/AggregatedGraphContext";
import { ScopeContext } from "../contexts/ScopeContext";
import { useControls } from "../hooks/useControls";
import { QueryResult } from "./QueryResult";

export const Explore: React.FC = () => {
  const { orgSlug, nodeType, nodeKey } = useParams();
  const { assessment, last } = useContext(SelectedEnvironmentContext);
  const { hasEnvironments } = useContext(EnvironmentDocsContext);
  const assessmentId = last.doc?.data.assessmentId;
  const { controls, setControls } = useControls();
  const { scopeKey } = useContext(ScopeContext);
  const { aggregated } = useContext(AggregatedGraphContext);

  const [selectedNodes, setSelectedNodes] = useState<
    AggregatedNode<AssessmentNodes, TargetNodeType, AnyAggregates>[]
  >([]);

  const action = useMemo(() => {
    if (!assessmentId || !orgSlug || !scopeKey) return undefined;
    return [
      DetailsColumn((data) => {
        const searchParams = new URLSearchParams(window.location.search);
        const base = `/o/${orgSlug}/${AppPaths.Inventory}`;
        const end = `/${encodeURIComponent(data.type)}/${encodeURIComponent(
          data.key
        )}?${searchParams.toString()}`;
        return {
          disabled: data.key === nodeKey,
          key: data.key,
          to: `${base}${end}`,
        };
      }),
    ];
  }, [assessmentId, nodeKey, orgSlug, scopeKey]);

  const extra = useMemo(() => ({ search: true }), []);

  const scopeNode = useMemo(() => <ScopeSelect includeAll={true} wide />, []);

  const hasGraph = assessment.doc?.data.hasGraph !== false;

  return hasEnvironments ? (
    <>
      <VerticalSpacedDiv style={{ gap: "18px", maxWidth: "1400px" }}>
        <Inventory />
        <AssessmentGraph
          aggregated={aggregated}
          controls={controls}
          extra={extra}
          extraColumns={action}
          onControls={setControls}
          onSelection={setSelectedNodes}
          scopeNode={scopeNode}
          selectedNodes={selectedNodes}
        />
      </VerticalSpacedDiv>
      {hasGraph && nodeKey && nodeType && <QueryResult />}
    </>
  ) : (
    <ZeroStatePage
      title="Discover assets in your cloud environments"
      description="Cloud inventory helps you discover and manage assets across your cloud environments."
      icon={<OrderedListOutlined />}
      docLink={DOCS_URLS.IAM_ASSESSMENT}
    />
  );
};
